import React, { useEffect, useMemo, useState } from 'react';
import { InvoicesTable } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { CustomerInvoice, InvoiceStatus } from './invoicesPaymentTypes';
import { useInvoicesStore } from '../../../store/invoicesStore';
import { Invoice, downloadInvoicePdf } from '../../../services/invoicesApi';
import { formatOrderId } from '../../../utils/formatId';
import { triggerBlobDownload } from '../../../utils/downloadBlob';
import styles from './CustomerInvoicesPayment.module.css';

type InvoiceTabId = 'all' | 'open' | 'due-soon' | 'past-due' | 'paid';

const TAB_FOR_STATUS: Record<InvoiceStatus, InvoiceTabId> = {
  Pending: 'open',
  'Due Soon': 'due-soon',
  Overdue: 'past-due',
  paid: 'paid',
};

const INVOICE_TABS: { id: InvoiceTabId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'due-soon', label: 'Due Soon' },
  { id: 'past-due', label: 'Past Due' },
  { id: 'paid', label: 'Paid' },
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).toUpperCase();
}

function formatCurrency(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function backendToUiStatus(b: Invoice): InvoiceStatus {
  if (b.status === 'paid') return 'paid';
  if (b.status === 'overdue') return 'Overdue';
  if (b.status === 'sent') {
    const due = new Date(new Date(b.invoice_date).getTime() + 30 * 24 * 60 * 60 * 1000);
    const diff = due.getTime() - Date.now();
    return diff < 7 * 24 * 60 * 60 * 1000 ? 'Due Soon' : 'Pending';
  }
  return 'Pending';
}

function adapt(b: Invoice): CustomerInvoice {
  const due = new Date(new Date(b.invoice_date).getTime() + 30 * 24 * 60 * 60 * 1000);
  const total = Number(b.total_amount);
  const paid = b.status === 'paid' ? total : 0;
  return {
    id: String(b.id),
    rawId: b.id,
    rawOrderId: b.order_id,
    invoiceNumber: b.invoice_number,
    orderNumber: formatOrderId(b.order_id),
    issueDate: formatDate(b.invoice_date),
    dueDate: formatDate(due.toISOString()),
    totalAmount: formatCurrency(total),
    paidAmount: formatCurrency(paid),
    balance: formatCurrency(total - paid),
    status: backendToUiStatus(b),
  };
}

const CustomerInvoicesPayment: React.FC = () => {
  const items = useInvoicesStore((s) => s.items);
  const loading = useInvoicesStore((s) => s.loading);
  const error = useInvoicesStore((s) => s.error);
  const setSource = useInvoicesStore((s) => s.setSource);
  const fetchList = useInvoicesStore((s) => s.fetchList);
  const [activeTab, setActiveTab] = useState<InvoiceTabId>('all');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    setSource('customer-self');
    fetchList();
  }, [setSource, fetchList]);

  const adapted = useMemo<CustomerInvoice[]>(() => items.map(adapt), [items]);

  const tabCounts = useMemo(() => {
    const counts: Record<InvoiceTabId, number> = { all: adapted.length, open: 0, 'due-soon': 0, 'past-due': 0, paid: 0 };
    for (const inv of adapted) counts[TAB_FOR_STATUS[inv.status]]++;
    return counts;
  }, [adapted]);

  const filtered = useMemo(() => {
    if (activeTab === 'all') return adapted;
    return adapted.filter((inv) => TAB_FOR_STATUS[inv.status] === activeTab);
  }, [adapted, activeTab]);

  // VP-CUST-005: wire the previously-dead download button to the real PDF
  // endpoint (GET /invoices/:id/pdf, Batch A5) via the same
  // apiClient.downloadBlob + triggerBlobDownload pattern already used for
  // the ledger/tax-compliance CSV exports.
  const handleDownload = async (invoiceId: string) => {
    const invoice = adapted.find((i) => i.id === invoiceId);
    setDownloadingId(invoiceId);
    setDownloadError(null);
    try {
      const blob = await downloadInvoicePdf(invoiceId);
      triggerBlobDownload(blob, `${invoice?.invoiceNumber ?? invoiceId}.pdf`);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Failed to download invoice');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className={styles.container}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {INVOICE_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '6px 14px',
              borderRadius: 999,
              border: activeTab === tab.id ? '1px solid #2563EB' : '1px solid #D1D5DB',
              background: activeTab === tab.id ? '#EFF6FF' : '#fff',
              color: activeTab === tab.id ? '#1D4ED8' : '#374151',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {tab.label} ({tabCounts[tab.id]})
          </button>
        ))}
      </div>

      {downloadError && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FEE2E2', color: '#B91C1C', fontSize: 13, marginBottom: 12 }}>
          {downloadError}
        </div>
      )}

      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && filtered.length === 0}
        emptyMessage="You have no invoices yet"
        onRetry={() => fetchList()}
      >
        <InvoicesTable
          invoices={filtered}
          onDownload={handleDownload}
        />
      </AsyncBoundary>
      {downloadingId && (
        <p style={{ fontSize: 12, color: '#6B7280', marginTop: 8 }}>Preparing {downloadingId}.pdf…</p>
      )}
    </div>
  );
};

export const CustomerInvoicesPaymentPage: React.FC = () => {
  return <CustomerInvoicesPayment />;
};

export { CustomerInvoicesPayment };
export default CustomerInvoicesPayment;
